import React, { useState } from 'react';
import {
  DecoratorNode,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';

export interface ExplainerPayload {
  topic: string;
  content: string;
  sources?: string[];
}

export type SerializedExplainerNode = Spread<
  { topic: string; content: string; sources: string[] },
  SerializedLexicalNode
>;

function ExplainerComponent({
  topic,
  content,
  sources,
}: {
  topic: string;
  content: string;
  sources: string[];
  nodeKey: string;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      style={{
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        padding: '16px 20px',
        margin: '24px 0',
      }}
    >
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          gap: '8px',
          fontWeight: 600,
          color: '#475569',
          fontSize: '15px',
        }}
      >
        <span style={{ fontSize: '18px' }}>💡</span>
        <span>What is {topic}?</span>
        <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#94a3b8' }}>
          {expanded ? '▼' : '▶'}
        </span>
      </div>
      {expanded && (
        <>
          <div style={{ marginTop: '12px', color: '#475569', lineHeight: 1.7, fontSize: '15px' }}>
            {content}
          </div>
          {sources.length > 0 && (
            <div style={{ marginTop: '12px', fontSize: '13px', color: '#94a3b8' }}>
              Sources: {sources.map((s, i) => (
                <span key={i}>
                  {i > 0 && ', '}
                  <a href={s} target="_blank" rel="noopener noreferrer" style={{ color: '#64748b' }}>
                    {new URL(s).hostname}
                  </a>
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export class ExplainerNode extends DecoratorNode<React.ReactElement> {
  __topic: string;
  __content: string;
  __sources: string[];

  static getType(): string {
    return 'explainer';
  }

  static clone(node: ExplainerNode): ExplainerNode {
    return new ExplainerNode(node.__topic, node.__content, node.__sources, node.__key);
  }

  constructor(topic: string, content: string, sources: string[] = [], key?: NodeKey) {
    super(key);
    this.__topic = topic;
    this.__content = content;
    this.__sources = sources;
  }

  static importJSON(serialized: SerializedExplainerNode): ExplainerNode {
    return new ExplainerNode(serialized.topic, serialized.content, serialized.sources || []);
  }

  exportJSON(): SerializedExplainerNode {
    return {
      type: 'explainer',
      version: 1,
      topic: this.__topic,
      content: this.__content,
      sources: this.__sources,
    };
  }

  createDOM(): HTMLElement {
    const div = document.createElement('div');
    div.className = 'explainer-node';
    return div;
  }

  updateDOM(): boolean {
    return false;
  }

  decorate(): React.ReactElement {
    return (
      <ExplainerComponent
        topic={this.__topic}
        content={this.__content}
        sources={this.__sources}
        nodeKey={this.__key}
      />
    );
  }
}

export function $createExplainerNode(payload: ExplainerPayload): ExplainerNode {
  return new ExplainerNode(payload.topic, payload.content, payload.sources || []);
}

export function $isExplainerNode(node: LexicalNode): node is ExplainerNode {
  return node instanceof ExplainerNode;
}
